import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { post, get, put, del, patch } from '../../api/base';
import { InformationLeadType, LeadFormType, RolType, TrialLeadType, UserType } from '../../types/types';
import { constants } from '../../constants/constants';

interface UserState {
  user: UserType;
  currentUser: UserType;
  users: UserType[];
  access: string | null;
  refresh: string | null;
  loading: boolean;
  error: any;
  loggedIn: boolean;
  rol: RolType;
}

export const initialState: UserState = {
  user: {
    id: null,
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    company_name: '',
    groups: [],
    is_staff: false,
    is_active: false,
    date_joined: '',
    company: null,
    callcode: null,
    phone: null,
    position: null,
    employer_name: null,
    employees: [],
  },
  currentUser: {
    id: null,
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    company_name: '',
    groups: [],
    is_staff: false,
    is_active: false,
    date_joined: '',
    company: null,
    callcode: null,
    phone: null,
    position: null,
    employer_name: null,
    employees: [],
  },
  users: [],
  access: localStorage.getItem('access') || null,
  refresh: localStorage.getItem('refresh') || null,
  loading: false,
  error: null,
  loggedIn: false,
  rol: null,
};

export const loginUser = createAsyncThunk(
  'user/loginUser',
  async (credentials: { username: string; password: string; captcha_token: string }, { rejectWithValue }) => {
    try {
      const response = await post('auth/', credentials);
      const { access, refresh } = response.data;
      // Guarda los tokens en localStorage
      localStorage.setItem('access', access);
      localStorage.setItem('refresh', refresh);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  }
);

export const recoverPassword = createAsyncThunk(
  'user/recoverPassword',
  async (data: { email: string; captcha_token: string }, { rejectWithValue }) => {
    try {
      await post('auth/reset-password/', { email: data.email, captcha_token: data.captcha_token });
      return { message: 'Si el email es correcto se enviará un mensaje de recuperación al correo.' };
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  }
);

export const requestTrial = createAsyncThunk(
  'user/requestTrial',
  async (
    data: {
      first_name: string;
      last_name: string;
      email: string;
      country_code: string;
      phone: string;
      company: string;
      captcha_token: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await post('public/request-trial/', data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || { message: 'No se pudo enviar la solicitud.' });
    }
  }
);

export const requestInformation = createAsyncThunk(
  'user/requestInformation',
  async (
    data: {
      name: string;
      email: string;
      phone: string;
      company: string;
      interest_topic: string;
      virtual_course_interests: string[];
      source: string;
      captcha_token: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await post('public/request-information/', data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || { message: 'No se pudo enviar la solicitud.' });
    }
  }
);

export const getLeadForms = createAsyncThunk(
  'user/getLeadForms',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await get('leads/forms/', {
        headers: headers,
      });
      return response.data as LeadFormType[];
    } catch (err: any) {
      return rejectWithValue(err.response?.data || { message: 'No se pudieron cargar los leads.' });
    }
  }
);

export const getLeadEntries = createAsyncThunk(
  'user/getLeadEntries',
  async (formKey: string, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await get(`leads/forms/${formKey}/`, {
        headers: headers,
      });
      return response.data as {
        key: string;
        name: string;
        entries: TrialLeadType[] | InformationLeadType[];
      };
    } catch (err: any) {
      return rejectWithValue(err.response?.data || { message: 'No se pudieron cargar los leads.' });
    }
  }
);

export const updateLeadEntry = createAsyncThunk(
  'user/updateLeadEntry',
  async (
    data: { formKey: string; leadId: number; contacted: boolean },
    { rejectWithValue, getState }
  ) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await patch(
        `leads/forms/${data.formKey}/${data.leadId}/`,
        { contacted: data.contacted },
        { headers }
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || { message: 'No se pudo actualizar el lead.' });
    }
  }
);

export const deleteLeadEntry = createAsyncThunk(
  'user/deleteLeadEntry',
  async (
    data: { formKey: string; leadId: number },
    { rejectWithValue, getState }
  ) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      await del(`leads/forms/${data.formKey}/${data.leadId}/`, { headers });
      return data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || { message: 'No se pudo eliminar el lead.' });
    }
  }
);

export const resetPassword = createAsyncThunk(
  'user/resetPassword',
  async (data: { new_password: string; token: string; uidb64: string }, { rejectWithValue }) => {
    try {
      await post(`auth/reset-password/${data.uidb64}/${data.token}/`, data);
      return { message: 'Contraseña actualizada correctamente.' };
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  }
);

export const refreshaccess = createAsyncThunk(
  'user/refreshaccess',
  async (refresh: string | null, { rejectWithValue }) => {
    if (!refresh) {
      return rejectWithValue('Token expired, please login again.');
    }
    try {
      const response = await post('auth/refresh/', { refresh: refresh });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  }
);

export const getInitialData = createAsyncThunk('user/getInitialData', async (_, { rejectWithValue, getState }) => {
  try {
    const state: any = getState();
    const headers = {
      Authorization: `Bearer ${state.user.access}`,
    };
    const response = await get('user/me/', {
      headers: headers,
    });
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response.data);
  }
});

export const updateMe = createAsyncThunk('user/updateMe', async (userData: UserType, { rejectWithValue, getState }) => {
  try {
    const state: any = getState();
    const headers = {
      Authorization: `Bearer ${state.user.access}`,
    };
    const response = await put('user/me/', userData, {
      headers: headers,
    });
    const apiUser = response.data?.user;
    // Backend devuelve claves camelCase y no siempre todos los campos; normalizamos y completamos con lo enviado
    const normalizedUser = apiUser
      ? {
          ...userData,
          first_name: apiUser.firstName ?? userData.first_name,
          last_name: apiUser.lastName ?? userData.last_name,
          username: apiUser.username ?? userData.username,
          email: apiUser.email ?? userData.email,
          id: apiUser.id ?? userData.id,
        }
      : userData;
    return normalizedUser;
  } catch (err: any) {
    return rejectWithValue(err.response.data);
  }
});

export const getAllUsers = createAsyncThunk('user/getAllUsers', async (_, { rejectWithValue, getState }) => {
  try {
    const state: any = getState();
    const headers = {
      Authorization: `Bearer ${state.user.access}`,
    };
    const response = await get('users/', {
      headers: headers,
    });
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response.data);
  }
});

export const updateUser = createAsyncThunk('user/update', async (userData: UserType, { rejectWithValue, getState }) => {
  try {
    const state: any = getState();
    const headers = {
      Authorization: `Bearer ${state.user.access}`,
    };
    const response = await put(`user/${userData.id}/`, userData, {
      headers: headers,
    });
    return response.data;
  } catch (err: any) {
    console.log(err);
    return rejectWithValue(err.response.data);
  }
});

export const createUser = createAsyncThunk('user/create', async (userData: UserType, { rejectWithValue, getState }) => {
  try {
    const state: any = getState();
    const headers = {
      Authorization: `Bearer ${state.user.access}`,
    };
    const response = await post('users/', userData, {
      headers: headers,
    });
    return response.data;
  } catch (err: any) {
    console.log(err);
    return rejectWithValue(err.response.data);
  }
});

export const deleteUser = createAsyncThunk('user/delete', async (id: number, { rejectWithValue, getState }) => {
  try {
    const state: any = getState();
    const headers = {
      Authorization: `Bearer ${state.user.access}`,
    };
    await del(`user/${id}/`, {
      headers: headers,
    });
    return { id };
  } catch (err: any) {
    return rejectWithValue(err.response.data);
  }
});

export const activateOrDeactivateUser = createAsyncThunk(
  'user/activate-or-deactivate',
  async (id: number, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      await put(`user/${id}/activate-or-deactivate`, null, {
        headers: headers,
      });
      return { id };
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  }
);

export const setCurrentUser = (user: UserType) => {
  return {
    type: 'user/setCurrentUser',
    payload: user,
  };
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    logoutUser: (state) => {
      state.user = initialState.user;
      state.access = null;
      state.refresh = null;
      localStorage.removeItem('access');
      localStorage.removeItem('refresh');
      state.loggedIn = false;
      state.rol = null;
      state.users = [];
      state.currentUser = initialState.currentUser;
    },
    setCurrentUser: (state, action) => {
      state.currentUser = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.access = action.payload.access;
        state.refresh = action.payload.refresh;
        state.error = null;
        state.loggedIn = true;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.loggedIn = false;
      })
      .addCase(refreshaccess.fulfilled, (state, action) => {
        state.access = action.payload.access;
        state.loggedIn = true;
      })
      .addCase(refreshaccess.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loggedIn = false;
      })
      .addCase(getInitialData.fulfilled, (state, action) => {
        state.user.id = action.payload.id;
        state.user.username = action.payload.username;
        state.user.email = action.payload.email;
        state.user.first_name = action.payload.first_name;
        state.user.last_name = action.payload.last_name;
        state.user.groups = action.payload.groups;
        state.user.is_staff = action.payload.is_staff;
        state.user.is_active = action.payload.is_active;
        state.user.company = action.payload.company;
        state.user.callcode = action.payload.callcode;
        state.user.phone = action.payload.phone;
        state.user.position = action.payload.position;
        state.user.company_name = action.payload.company_name;
        state.user.employer_name = action.payload.employer_name;
        state.user.employees = action.payload.employees;
        state.rol = action.payload.is_staff ? constants.ROL_ADMIN : getRol(action.payload.groups);
        state.error = null;
      })
      .addCase(getInitialData.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(updateMe.fulfilled, (state, action) => {
        state.user = { ...state.user, ...action.payload };
        state.currentUser = { ...state.currentUser, ...action.payload };
        state.rol = action.payload.is_staff ? constants.ROL_ADMIN : getRol(action.payload.groups);
        state.error = null;
      })
      .addCase(updateMe.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(getAllUsers.fulfilled, (state, action) => {
        state.users = action.payload;
        state.error = null;
      })
      .addCase(getAllUsers.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.error = null;
      })
      .addCase(createUser.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.users = state.users.filter((user) => user.id !== action.payload.id);
        state.error = null;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.users.push(action.payload);
        state.error = null;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(activateOrDeactivateUser.fulfilled, (state, action) => {
        state.users = state.users.map((user) =>
          user.id === action.payload.id ? { ...user, is_active: !user.is_active } : user
        );
        state.error = null;
      })
      .addCase(activateOrDeactivateUser.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(recoverPassword.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(recoverPassword.fulfilled, (state, action) => {
        state.error = null;
      })
      .addCase(recoverPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestTrial.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestTrial.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(requestTrial.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const loginAndFetchData = (credentials: { username: string; password: string; captcha_token: string }) => async (dispatch: any) => {
  const result = await dispatch(loginUser(credentials));

  if (loginUser.fulfilled.match(result)) {
    return await dispatch(getInitialData());
  }
  throw new Error(result.payload?.detail || result.payload?.message || 'El usuario o la contraseña son incorrectas.');
};

export const refreshAndFetchData = (refresh: string | null) => async (dispatch: any) => {
  const result = await dispatch(refreshaccess(refresh));

  if (refreshaccess.fulfilled.match(result)) {
    return await dispatch(getInitialData());
  }
  throw new Error('El usuario o la contraseña son incorrectas.');
};

export const getRol = (groups: Array<number>) => {
  return groups?.includes(1)
    ? constants.ROL_ADMIN
    : groups?.includes(3)
    ? constants.ROL_USER
    : groups?.includes(2)
    ? constants.ROL_EMPRESA
    : null;
};

export const { logoutUser } = userSlice.actions;
export default userSlice.reducer;
